/**
 * Employer Dashboard Page
 *
 * Main dashboard for employees showing:
 * - Quick actions
 * - Benefit program enrollments
 * - Recommendations
 * - Upcoming deadlines
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Gift,
  Compass,
  Sparkles,
  Heart,
  Calendar,
  ArrowRight,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  Star,
  Briefcase,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployerLayout from '../layout';

interface BenefitProgram {
  id: number;
  program_code: string;
  program_name: string;
  program_type: string;
  program_category: string;
  status: string;
  enrollment_status?: string;
}

interface Recommendation {
  id: number;
  program_name: string;
  score: number;
  reasons: string[];
  status: string;
}

export default function EmployerDashboardPage() {
  const [benefitPrograms, setBenefitPrograms] = useState<BenefitProgram[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState<any>(null);

  const member = getMemberData();

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch employee data
        const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
        const employeeResponse = await fetch(employeeUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (employeeResponse.ok) {
          const empData = await employeeResponse.json();
          setEmployeeData(empData);

          // Fetch benefit programs with enrollment status
          const benefitsUrl = getWeaveinApiUrl('/api/member-portal/benefits');
          const benefitsResponse = await fetch(benefitsUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json',
            },
          });

          if (benefitsResponse.ok) {
            const programsData = await benefitsResponse.json();
            setBenefitPrograms(programsData.slice(0, 4)); // Show top 4
          }

          // Fetch recommendations
          const recsUrl = getWeaveinApiUrl(`/api/employees/${empData.id}/recommendations`);
          const recsResponse = await fetch(recsUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json',
            },
          });

          if (recsResponse.ok) {
            const recsData = await recsResponse.json();
            setRecommendations(recsData.slice(0, 3)); // Show top 3
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [member?.id]);

  const quickActions = [
    { icon: Gift, label: 'Browse Benefits', path: '/employer/benefits', color: 'bg-blue-500' },
    { icon: Compass, label: 'Get Navigation Help', path: '/employer/navigation', color: 'bg-green-500' },
    { icon: Sparkles, label: 'View Recommendations', path: '/employer/recommendations', color: 'bg-purple-500' },
    { icon: Briefcase, label: 'Leave & Disability', path: '/employer/leave', color: 'bg-orange-500' },
  ];

  const upcomingDeadlines = [
    { title: 'Open Enrollment Ends', date: 'Dec 15, 2025', type: 'enrollment' },
    { title: 'FSA Contribution Deadline', date: 'Dec 31, 2025', type: 'financial' },
    { title: 'Wellness Challenge', date: 'Jan 15, 2026', type: 'wellness' },
  ];

  return (
    <EmployerLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">
            Welcome back, {member?.first_name || 'Employee'}!
          </h1>
          <p className="text-indigo-100 mb-4">
            Explore your benefits, get personalized recommendations, and access support services.
          </p>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((action, idx) => (
              <Link
                key={idx}
                to={action.path}
                className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <action.icon className="w-4 h-4 mr-2" />
                {action.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Your Benefits */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Your Benefits</h2>
                <Link
                  to="/employer/benefits"
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                >
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {loading ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-24" />
                  ))}
                </div>
              ) : benefitPrograms.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {benefitPrograms.map((program) => (
                    <div
                      key={program.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">{program.program_name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{program.program_category}</p>
                        </div>
                        {program.enrollment_status === 'ENROLLED' ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Enrolled
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            Available
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No benefit programs available yet.</p>
                  <Link
                    to="/employer/benefits"
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2 inline-block"
                  >
                    Browse Available Benefits
                  </Link>
                </div>
              )}
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Sparkles className="w-5 h-5 text-purple-500 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Recommended For You</h2>
                </div>
                <Link
                  to="/employer/recommendations"
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                >
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-20" />
                  ))}
                </div>
              ) : recommendations.length > 0 ? (
                <div className="space-y-3">
                  {recommendations.map((rec) => (
                    <div
                      key={rec.id}
                      className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">{rec.program_name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {rec.reasons?.[0] || 'Personalized recommendation based on your profile'}
                          </p>
                        </div>
                        <div className="flex items-center text-purple-600">
                          <Star className="w-4 h-4 mr-1 fill-current" />
                          <span className="text-sm font-medium">{Math.round(rec.score * 100)}% match</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Recommendations are being generated...</p>
                  <p className="text-sm text-gray-400 mt-1">Check back soon for personalized suggestions.</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Activity</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                    <span>Programs Enrolled</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {benefitPrograms.filter(p => p.enrollment_status === 'ENROLLED').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600">
                    <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
                    <span>Wellness Points</span>
                  </div>
                  <span className="font-semibold text-gray-900">250</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600">
                    <Compass className="w-5 h-5 mr-2 text-purple-500" />
                    <span>Navigation Sessions</span>
                  </div>
                  <span className="font-semibold text-gray-900">3</span>
                </div>
              </div>
            </div>

            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
              <div className="space-y-3">
                {upcomingDeadlines.map((deadline, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Clock className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{deadline.title}</p>
                      <p className="text-xs text-gray-500">{deadline.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Need Help? */}
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl border border-green-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Our navigation team is here to help you find the right benefits and resources.
              </p>
              <Link
                to="/employer/navigation"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <Compass className="w-4 h-4 mr-2" />
                Start Navigation Session
              </Link>
            </div>
          </div>
        </div>
      </div>
    </EmployerLayout>
  );
}
